/**
 * Vendor Intelligence — investigation legend & conclusion explainer.
 * PRESENTATION ONLY.
 *
 * This module contains no logic, no calculation, no evidence and no
 * data access. It turns badge words that are ALREADY rendered inside an
 * investigation into plain-English definitions, and renders the
 * standardized "How This Conclusion Was Reached" explainer.
 *
 * Rule: only badges that actually appear inside an investigation may be
 * defined. Nothing is invented and no default confidence is supplied.
 */

import type { ReactNode } from "react";
import { Info } from "lucide-react";
import { NOT_CALCULATED, confidenceDescriptor } from "@/lib/confidence/confidence-vocabulary";
import type { InvestigationState } from "./investigation-status";

/** Canonical plain-English badge dictionary. */
const BADGE_DEFINITIONS: Array<{ label: string; meaning: string }> = [
  {
    label: "Verified",
    meaning: "Information confirmed using one or more connected sources.",
  },
  {
    label: "Matched",
    meaning: "Information matched across multiple independent records.",
  },
  {
    label: "Reviewed",
    meaning:
      "ClaimGuard reviewed this information but could not independently verify it.",
  },
  {
    label: "Evidence Source",
    meaning:
      "This source contributed information to the investigation. It supports the analysis but does not independently verify every conclusion.",
  },
  {
    label: "Low Risk",
    meaning: "No material concerns affecting this invoice were identified.",
  },
  {
    label: "No Evidence Found",
    meaning:
      "The investigation completed successfully and no evidence supporting this concern was found.",
  },
  {
    label: "Awaiting Provider",
    meaning:
      "ClaimGuard cannot currently access the provider required to complete this investigation. No negative inference should be made.",
  },
  { label: "Informational", meaning: "Background information provided for context." },
  {
    label: "Above Market",
    meaning: "Charge exceeds regional benchmark pricing.",
  },
  {
    label: "Below Market",
    meaning: "Charge falls below regional benchmark pricing.",
  },
  {
    label: "Market Average",
    meaning: "Charge aligns with regional benchmark pricing.",
  },
  {
    label: "Benchmark Coverage",
    meaning:
      "Percentage of invoice charges that could be compared against available market benchmarks.",
  },
  {
    label: "Comparable Charges",
    meaning: "Charges with verified regional pricing available.",
  },
];

const norm = (s: string) => s.trim().toLowerCase();

/**
 * Keep only the definitions whose badge actually appears in this
 * investigation. Unknown words are ignored — never explained generically.
 */
export function legendEntriesFor(
  appearing: Array<string | null | undefined>,
): Array<{ label: string; meaning: string }> {
  const seen = new Set(appearing.filter(Boolean).map((s) => norm(s as string)));
  return BADGE_DEFINITIONS.filter((d) => seen.has(norm(d.label)));
}

/** ⓘ How To Read This Investigation — legend toolbar (collapsed by default). */
export function InvestigationLegend({
  entries,
  className = "",
}: {
  entries: Array<{ label: string; meaning: string }>;
  className?: string;
}) {
  if (entries.length === 0) return null;
  return (
    <details
      data-pdf-include="true"
      data-investigation-legend="true"
      className={`group rounded-2xl border border-trust/25 bg-trust-soft/60 p-4 md:p-5 transition-colors ${className}`}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-trust">
        <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
        How To Read This Investigation
      </summary>
      <dl className="mt-3 grid gap-2.5 sm:grid-cols-2">
        {entries.map((e) => (
          <div
            key={e.label}
            className="rounded-xl border border-trust/15 bg-card/70 p-3"
          >
            <dt className="text-[11px] font-bold uppercase tracking-[0.14em] text-navy">
              {e.label}
            </dt>
            <dd className="mt-1 text-xs leading-5 text-muted-foreground">{e.meaning}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/** Plain-English basis sentence, driven only by the state already shown. */
function basisFor(state?: InvestigationState | null): string | null {
  switch (state) {
    case "verified":
      return "Multiple independent records agreed across the connected sources listed above.";
    case "no_evidence":
      return "The investigation completed against the sources listed above and no evidence indicating increased invoice risk was identified.";
    case "awaiting_provider":
      return "This investigation requires a connected provider that ClaimGuard could not access. The investigation was limited accordingly and no adverse inference was made.";
    case "informational":
      return "This investigation provides background context only and does not by itself support or dispute any charge.";
    default:
      return null;
  }
}

/** "How This Conclusion Was Reached" — standardized explainer. */
export function ConclusionBasis({
  evidenceReviewed,
  sources,
  confidencePct,
  limitation,
  state,
  className = "",
}: {
  evidenceReviewed?: string | null;
  sources: string[];
  /** Measured confidence only. Omitted values render Not Calculated. */
  confidencePct?: number | null;
  limitation?: string | null;
  state?: InvestigationState | null;
  className?: string;
}) {
  const rows: Array<{ label: string; body: ReactNode }> = [];
  if (evidenceReviewed) rows.push({ label: "Evidence Reviewed", body: evidenceReviewed });
  if (sources.length > 0)
    rows.push({
      label: "Evidence Sources",
      body: (
        <ul className="space-y-1">
          {sources.map((s) => (
            <li key={s} className="flex items-start gap-2">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-border" />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      ),
    });
  const basis = basisFor(state);
  if (basis) rows.push({ label: "Why This Conclusion Was Reached", body: basis });
  rows.push({
    label: "Overall Confidence",
    body:
      typeof confidencePct === "number"
        ? confidenceDescriptor(confidencePct)
        : NOT_CALCULATED,
  });
  if (limitation) rows.push({ label: "Investigation Limitations", body: limitation });

  return (
    <div
      data-pdf-include="true"
      data-conclusion-basis="true"
      className={`print-avoid-break rounded-2xl border border-border/70 bg-secondary/25 p-5 md:p-6 ${className}`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-navy">
        How This Conclusion Was Reached
      </div>
      <dl className="mt-3 grid gap-4 md:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label}>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {r.label}
            </dt>
            <dd className="mt-1 text-sm leading-6 text-foreground/85">{r.body}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
